import { inject, InjectionKey, provide, Ref } from "vue";
import { assign, createMachine, InterpreterFrom, send, choose } from "xstate-4";
import { useActor, useInterpret } from "@xstate-4/vue";
import {
  MachineInboxConversationsContext,
  machineInboxConversations,
} from "vue-app/components/Inbox/machines/machineInboxConversations";
import { machineInboxChat } from "vue-app/components/Inbox/machines/machineInboxChat";
import { machineInboxInstructions } from "vue-app/components/Inbox/machines/machineInboxInstructions";
import { xlog } from "vue-app/utils/xstate";
import axios from "vue-app/utils/axios";
import { ReactNativeBridge } from "vue-app/utils/reactNativeBridge";

export type Integrations = {
  email: boolean;
  facebook: boolean;
  integrated: boolean;
  inbox: boolean;
  allegro: boolean;
  whatsapp: boolean;
};

export type Agent = {
  agentId: number;
  email: string;
  firstName: string;
  lastName: string;
  avatar: string | null;
};

export type Conversation = {
  inboxConversationId: number;
  trackerId: number;
  customerId: number;
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  inboxConversationStatus: string;
  allegroAnswerable: boolean | null;
  allegroLogin: string | null;
  read: boolean;
  unreadMessagesCount: number;
  lastInboxConversationMessageId: number;
  lastMessageDate: number;
  message: string;
  sourceType: "EMAIL" | "CHAT" | "FACEBOOK" | "WHATSAPP" | "ALLEGRO" | "ALLEGRO_DISPUTE" | "ALLEGRO_CLAIM" | "AI_AGENT"
  inboxConversationExternalStatus: "CLOSED" | "ONGOING" | "UNRESOLVED";
  conversationPhone: string;
  expirationDate: number;
  assignedAgentId: number;
  smsSubscriptionStatus: string;
  subscriptionStatus: string;
  conversationCustomers: ConversationCustomer[];
  snoozedUntil: number;
  closingDate: number;
  whatsappMessageType: string;
  activeAgentIds?: number[];
  agentTakeoverDate?: string | null;
  test?: boolean;
};

export type ConversationCustomer = {
  inboxConversationId: number;
  customerId: number;
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  subscriptionStatus: string;
  smsSubscriptionStatus: string;
  externalId: string | null;
};

type Context = {
  integrations: Integrations | null;
  selectedConversation: Conversation | null;
  isMobile: boolean;
  initialConversationId: number | null;
  pendingConversationUpdates: Set<number>;
  currentUpdatingConversationId: number | null;
};
export const machinePageInbox = createMachine(
  {
    id: "Inbox",
    tsTypes: {} as import("./machinePageInbox.typegen").Typegen0,
    context: {
      integrations: null,
      selectedConversation: null,
      isMobile: false,
      initialConversationId: null,
      pendingConversationUpdates: new Set<number>(),
      currentUpdatingConversationId: null,
    },
    schema: {
      events: {} as
        | { type: "SELECT_CONVERSATION"; conversationData: Conversation }
        | { type: "SELECT_CONVERSATION_BY_ID"; conversationId: number }
        | { type: "UPDATE_CONVERSATION"; conversationId: number }
        | { type: "MARK_AS_READ"; conversationData: Conversation }
        | { type: "RETRY" }
        | { type: "RELOAD" },
      context: {} as Context,
      services: {} as {
        checkIntegrations: {
          data: Integrations;
        };
        updatingConversation: {
          data: Conversation;
        };
        getConversation: {
          data: Conversation;
        };
        getAgentList: {
          data: Agent[];
        };
        inboxConversations: {
          data: MachineInboxConversationsContext;
        };
      },
    },
    initial: "checkingIntegrations",
    entry: ["assignIsMobile"],
    states: {
      checkingIntegrations: {
        tags: ["checkingIntegrations"],
        invoke: {
          src: "checkIntegrations",
          id: "checkIntegrations",
          onDone: [
            {
              target: "inbox",
              actions: ["assignIntegrations"],
              cond: "isIntegrated",
            },
            {
              target: "notIntegrated",
              actions: ["assignIntegrations"],
            },
          ],
          onError: {
            target: "errorPage",
          },
        },
      },
      inbox: {
        tags: ["inbox"],
        type: "parallel",
        entry: ["loadInitialConversation"],
        states: {
          conversations: {
            tags: ["conversations"],
            invoke: {
              id: "conversations",
              src: "inboxConversations",
            },
          },
          chat: {
            tags: ["chat"],
            invoke: {
              id: "chat",
              src: "inboxChat",
            },
          },
          instructions: {
            tags: ["instructions"],
            invoke: {
              id: "instructions",
              src: "inboxInstructions",
            },
          },
          getConversation: {
            tags: ["gettingConversation"],
            initial: "waiting",
            states: {
              waiting: {
                on: {
                  SELECT_CONVERSATION_BY_ID: {
                    target: "gettingConversation",
                  },
                },
              },
              gettingConversation: {
                invoke: {
                  src: "getConversation",
                  id: "getConversation",
                  onDone: [
                    {
                      target: "waiting",
                      actions: [send((ctx, event) => ({ type: "SELECT_CONVERSATION", conversationData: event.data }))],
                    },
                  ],
                },
              },
            },
          },
          updateConversation: {
            tags: ["updatingConversation"],
            initial: "waiting",
            states: {
              waiting: {
                always: {
                  target: "updatingConversation",
                  cond: (context) => context.pendingConversationUpdates.size > 0,
                },
              },
              updatingConversation: {
                entry: "popFromPendingUpdates",
                invoke: {
                  src: "updatingConversation",
                  id: "updatingConversation",
                  onDone: [
                    {
                      // sending conversation data to chat and conversations machine selected conversation is same as updated conversation
                      target: "waiting",
                      actions: [
                        send((ctx, event) => ({ type: "UPDATE_CONVERSATION", conversationData: event.data }), {
                          to: "conversations",
                        }),
                        send((ctx, event) => ({ type: "UPDATE_CONVERSATION", conversationData: event.data }), {
                          to: "chat",
                        }),
                      ],
                      cond: (context, event) =>
                        event.data.inboxConversationId === context.selectedConversation?.inboxConversationId,
                    },
                    {
                      // sending conversation data only to conversations machine
                      target: "waiting",
                      actions: [
                        send((ctx, event) => ({ type: "UPDATE_CONVERSATION", conversationData: event.data }), {
                          to: "conversations",
                        }),
                      ],
                    },
                  ],
                  onError: [
                    {
                      target: "waiting",
                      actions: [
                        () => console.error("Some message could not be updated. Please reload conversation list."),
                      ],
                    },
                  ],
                },
              },
            },
          },
          loadAgents: {
            tags: ["loadingAgents"],
            initial: "getAgentList",
            states: {
              done: {
                type: "final",
              },
              getAgentList: {
                invoke: {
                  src: "getAgentList",
                  id: "getAgentList",
                  onDone: {
                    target: "done",
                    actions: [
                      send((ctx, event) => ({ type: "LOAD_AGENTS", agentList: event.data }), {
                        to: "chat",
                      }),
                    ],
                  },
                  onError: {
                    target: "done",
                    actions: [() => console.error("Error while trying to load agents.")],
                  },
                },
              },
            },
          },
        },
        on: {
          SELECT_CONVERSATION: [
            {
              actions: [
                "assignSelectedConversation",
                send((ctx, event) => ({ type: "SELECT_CONVERSATION", conversationData: event.conversationData }), {
                  to: "chat",
                }),
              ],
            },
          ],
          UPDATE_CONVERSATION: {
            actions: ["addToPendingUpdates", "refreshMenuBadges"],
            cond: "isNotDemo",
          },
          MARK_AS_READ: {
            actions: [
              send((ctx, event) => ({ type: "UPDATE_CONVERSATION", conversationData: event.conversationData }), {
                to: "conversations",
              }),
              "refreshMenuBadges",
            ],
          },
        },
      },
      notIntegrated: {
        tags: ["notIntegrated"],
        initial: "instructions",
        states: {
          instructions: {
            tags: ["instructions"],
            invoke: {
              id: "instructions",
              src: "inboxInstructions",
            },
          },
        },
        on: {
          RELOAD: {
            target: "checkingIntegrations",
          },
        },
      },
      errorPage: {
        tags: ["errorPage"],
        on: {
          RETRY: {
            target: "checkingIntegrations",
          },
        },
      },
    },
    predictableActionArguments: true,
    preserveActionOrder: true,
  },
  {
    actions: {
      assignIntegrations: assign((context, event) => {
        xlog({ label: "assignIntegrations", type: "action", context, event });
        return {
          integrations: event.data,
        };
      }),
      assignSelectedConversation: assign((context, event) => {
        xlog({ label: "assignSelectedConversation", type: "action", context, event });
        return {
          selectedConversation: event.conversationData,
        };
      }),
      assignIsMobile: assign((context, event) => {
        xlog({ label: "assignIsMobile", type: "action", context, event });
        return {
          isMobile: ReactNativeBridge.checkIsWebViewMode() ? false : document.documentElement.clientWidth < 992,
        };
      }),
      refreshMenuBadges: () => {
        window.pubSub.publish("menu.badges.refresh");
      },
      addToPendingUpdates: assign({
        pendingConversationUpdates: (context, event) => {
          xlog({ label: "addToPendingUpdates", type: "action", context, event });
          const newSet = new Set(context.pendingConversationUpdates);
          newSet.add(event.conversationId);
          return newSet;
        },
      }),
      popFromPendingUpdates: assign((context) => {
        xlog({ label: "popFromPendingUpdates", type: "action", context });
        const conversationId = context.pendingConversationUpdates.values().next().value;
        const newSet = new Set(context.pendingConversationUpdates);
        if (conversationId) {
          newSet.delete(conversationId);
        }
        return {
          pendingConversationUpdates: newSet,
          currentUpdatingConversationId: conversationId ?? null,
        };
      }),
      loadInitialConversation: choose([
        {
          cond: (context) => context.initialConversationId !== null,
          actions: [
            send((context) => ({
              type: "SELECT_CONVERSATION_BY_ID" as const,
              conversationId: context.initialConversationId!,
            })),
          ],
        },
      ]),
    },
    guards: {
      isIntegrated: (context, event) => {
        xlog({ label: "isIntegrated", type: "guard", context, event });
        return event.data !== null && event.data.integrated;
      },
      isNotDemo: (context, event) => {
        xlog({ label: "isDemo", type: "guard", context, event });
        return !window.EDRONE_CONTEXT.isDemo;
      },
    },
    services: {
      inboxConversations: (context) => {
        return machineInboxConversations.withContext({
          ...machineInboxConversations.context,
          integrations: context.integrations,
        });
      },
      inboxChat: machineInboxChat,
      inboxInstructions: machineInboxInstructions,
      checkIntegrations: async (context, event) => {
        xlog({ label: "checkIntegrations", context, event });
        const response = await axios.get("/inbox/get-integration-inbox-info");
        return response.data.data;
      },
      updatingConversation: async (context, event) => {
        xlog({ label: "updatingConversation", context, event });
        const conversationId = context.currentUpdatingConversationId;
        if (!conversationId) {
          throw new Error("No conversation to update");
        }
        const response = await axios.get(`/inbox/get-conversation?inbox_conversation_id=${conversationId}`);
        return response.data.data;
      },
      getConversation: async (context, event) => {
        xlog({ label: "getConversation", context, event });
        const response = await axios.get(`/inbox/get-conversation?inbox_conversation_id=${event.conversationId}`);
        return response.data.data;
      },
      getAgentList: async (context, event) => {
        xlog({ label: "getAgentList", type: "action", context, event });
        const response = await axios.get("/inbox/get-agents");
        return response.data.data;
      },
    },
  }
);

export function mapConversationCustomers(customers: ConversationCustomer[]) {
  if (!customers) return [];
  return customers.map((customer) => {
    return {
      ...customer,
      email:
        customer.email.toLowerCase().includes("chatcustomer") ||
        customer.email.includes("customer-facebook-messenger") ||
        customer.email.includes("whatsapp.edrone.me") ||
        customer.email.includes("phone.edrone.me")
          ? ""
          : customer.email,
    };
  });
}

export function getCustomersFromArray(customers: ConversationCustomer[], type: "other" | "names" = "names") {
  return customers
    .map((customer) => {
      if (type === "names") {
        return customer.firstName || customer.lastName
          ? [customer.firstName, customer.lastName].filter((name) => !!name).join(" ")
          : window._trans._inbox.inboxConversations.conversation.noName;
      } else {
        return (
          customer.email ||
          customer.phone ||
          "#" + customer.customerId ||
          window._trans._inbox.inboxConversations.conversation.noContact
        );
      }
    })
    .join(", ");
}

export type PageInboxActor = InterpreterFrom<typeof machinePageInbox>;
export const inboxServiceSymbol = Symbol() as InjectionKey<PageInboxActor>;

export function provideInboxActor(context?: Partial<Context>): {
  send: PageInboxActor["send"];
  state: Ref<PageInboxActor["state"]>;
} {
  // @ts-ignore
  const actor = useInterpret(machinePageInbox, {
    devTools: true,
    context: { ...machinePageInbox.context, ...context },
  });
  // @ts-ignore
  provide(inboxServiceSymbol, actor);
  // @ts-ignore
  return useActor(actor);
}

export function useInboxActor() {
  return inject<PageInboxActor>(inboxServiceSymbol);
}
